import { Svg } from "@client/components/Svg";
import React from "react";
import { t } from "ttag";

import { ImageTextCarouselBlock as Value } from "@reactivated";

import { notEmptyOrBlank } from "@thelabnyc/thelabui/src/utils/functional";
import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { ListCarousel } from "../Carousel/ListCarousel";
import { Clickable } from "../Clickables";
import { MediaChooserBlock } from "../MediaChooserBlock";
import { RichText } from "../RichText";

import styles from "./index.module.scss";

type SlideProps = NonNullable<Value["slides"]>[number]["value"];

const Slide = (props: SlideProps) => {
    const media =
        props.media && props.media.length > 0 ? (
            <MediaChooserBlock
                wrapperAttrs={{ className: styles.mediaWrapper }}
                value={props.media}
                type="background"
                videoControlAttrs={{ className: styles.videoControls }}
            />
        ) : null;

    const href = props.cta?.url ?? props.cta?.anchor;
    const wrapperClass =
        media === null ? styles.gradientSlide : styles.mediaSlide;
    const content = props.content && (
        <RichText className={styles.richText} value={props.content} />
    );

    return props.cta && href ? (
        <Clickable href={href} className={wrapperClass}>
            {media}
            {content}
            <span className={styles.fakeBorderless}>
                <span className="clickableContent">
                    {props.cta.display_name}
                    <Svg
                        name="arrow"
                        visuallyHiddenText={null}
                        aria-hidden={true}
                    />
                </span>
            </span>
        </Clickable>
    ) : (
        <div className={wrapperClass}>
            {media}
            {content}
        </div>
    );
};

const slidesVisibleDesktop = 2.333;
const slidesVisibleTablet = 1.5;
const slidesVisibleMobile = 1.25;

interface CustomCss extends React.CSSProperties {
    "--slides-visible-desktop": number;
    "--slides-visible-tablet": number;
    "--slides-visible-mobile": number;
}

export const ImageTextCarouselBlock = ({
    value: { block_name, content, slides, theme },
}: {
    value: Value;
}) => {
    if (!slides) return <></>;

    const slideLength = slides.filter(notEmptyOrBlank).length;

    /**
     * If we don't have enough slides to trigger the scroll, we want to center
     * the items, but I couldn't figure out how to do that without changing
     * the CSS.
     */
    let noScrollBreakpoint = "";
    if (slideLength < slidesVisibleDesktop)
        noScrollBreakpoint = styles.noScrollDesktop;
    if (slideLength < slidesVisibleTablet)
        noScrollBreakpoint = styles.noScrollTablet;
    if (slideLength < slidesVisibleMobile)
        noScrollBreakpoint = styles.noScrollMobile;

    return (
        <section
            className={styles[theme ?? "match"]}
            style={
                {
                    "--slides-visible-desktop": slidesVisibleDesktop,
                    "--slides-visible-tablet": slidesVisibleTablet,
                    "--slides-visible-mobile": slidesVisibleMobile,
                } as CustomCss
            }
        >
            <div className={styles.header}>
                {content && <RichText value={content} />}
            </div>
            <div className={styles.inner}>
                <ListCarousel
                    label={block_name || t`Carousel`}
                    navigationProps={{ className: styles.carouselNav }}
                    wrapperAttrs={{
                        className: concatClassNames([
                            styles.carousel,
                            noScrollBreakpoint,
                        ]),
                    }}
                    listItemAttrs={{
                        className: concatClassNames([
                            styles.listItem,
                            slides.length === 1
                                ? styles.singleListItem
                                : undefined,
                        ]),
                    }}
                    dotsClassName={styles.dots}
                >
                    {slides.filter(notEmptyOrBlank).map((slide) => (
                        <Slide {...slide.value} key={slide.id} />
                    ))}
                </ListCarousel>
            </div>
        </section>
    );
};
